import React, { useState, useRef } from "react";
import logoImg from "../../assets/Component 1.svg";
import { useHistory } from "react-router-dom";
import Loading from "../../component/Loading/Loading";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../services/apiconfig";
import { ArrowLeft, Mail, Key, Send, KeyRound, ShieldCheck, AlertCircle } from "lucide-react";
import AlertMsg from '../../component/AlertMsg/AlertMsg';

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpSent, setOtpSent] = useState(false);
  const [otpError, setOtpError] = useState("");
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const otpRefs = useRef([]);
  const [alert, setAlert] = useState({ visible: false, message: '', type: '' });
  const [userId, setUserId] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "email") {
      setEmail(value);
      setEmailError("");
    } else if (name.startsWith("otp")) {
      const index = parseInt(name.charAt(3));
      const newOtp = [...otp];
      // Only allow digits
      if (value === "" || /^\d$/.test(value)) {
        newOtp[index] = value;
        setOtp(newOtp);
        setOtpError("");

        if (value && index < otp.length - 1) {
          otpRefs.current[index + 1]?.focus();
        }
      }
    }
  };

  const handleKeyDown = (e, index) => {
    // Handle backspace to go to previous input
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    // Handle Arrow keys for navigation
    if (e.key === "ArrowLeft" && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowRight" && index < otp.length - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSendOtp = async () => {
    if (!isValidEmail(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/user/forgot-password?emailId=${encodeURIComponent(email)}`,
        {} // Empty body
      );

      setUserId(response.data.userId);
      setOtpSent(true);
      setAlert({
        visible: true,
        message: 'OTP sent successfully! Please check your email.',
        type: 'success'
      });
    } catch (error) {
      console.error("Error sending OTP:", error);
      if (error.response && error.response.status === 404) {
        setEmailError("Invalid email. Please enter a valid email address.");
        setAlert({
          visible: true,
          message: 'Email not found in our system.',
          type: 'error'
        });
      } else {
        setAlert({
          visible: true,
          message: 'Failed to send OTP. Please try again.',
          type: 'error'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEnterOTP = async () => {
    const enteredOtp = otp.join("");
    if (enteredOtp.length !== 6 || !/^\d+$/.test(enteredOtp)) {
      setOtpError("Please enter a valid 6-digit OTP.");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/user/verify-otp?userId=${userId}&otp=${enteredOtp}`,
        {} // Empty body
      );

      setAlert({
        visible: true,
        message: 'OTP verified successfully!',
        type: 'success'
      });

      // Redirect after short delay to show success message
      setTimeout(() => {
        history.push(`/confirm-password/${userId}`);
      }, 1000);

    } catch (error) {
      setOtpError("Invalid OTP. Please try again.");
      setAlert({
        visible: true,
        message: 'Invalid OTP. Please try again.',
        type: 'error'
      });
      console.error("Error verifying OTP:", error);
    } finally {
      setLoading(false);
    }
  };

  const resendOTP = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/user/forgot-password?emailId=${encodeURIComponent(email)}`,
        {} // Empty body
      );

      // Reset OTP fields
      setOtp(["", "", "", "", "", ""]);

      setAlert({
        visible: true,
        message: 'New OTP sent successfully!',
        type: 'success'
      });
    } catch (error) {
      setAlert({
        visible: true,
        message: 'Failed to resend OTP. Please try again.',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const pasteOTP = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');

    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setOtp(digits);
      // Focus the last input
      otpRefs.current[5]?.focus();
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen md:p-4 flex items-center justify-center">
      {loading && <Loading />}

      <div className="max-w-7xl mx-auto">
        {alert.visible && (
          <AlertMsg
            message={alert.message}
            type={alert.type}
            onClose={() => setAlert({ ...alert, visible: false })}
          />
        )}

        <div className="flex flex-col md:flex-row shadow-lg rounded-lg overflow-hidden">
          {/* Left side - Form */}
          <div className="w-full md:w-1/2 bg-white p-8">
            <div className="max-w-md mx-auto">
              <div className="flex justify-between items-center mb-6">
                <Link to="/" className="flex items-center text-gray-700 hover:text-blue-600 transition-colors">
                  <ArrowLeft size={18} className="mr-2" />
                  <span>Back to Login</span>
                </Link>
              </div>

              {/* Logo */}
              <div className="flex justify-center mb-8">
                <img src={logoImg} alt="Logo" className="h-16 scale-150" />
              </div>

              <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                {!otpSent ? "Forgot Password" : "Verify OTP"}
              </h2>

              {!otpSent ? (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail size={18} className="text-gray-400" />
                      </div>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={email}
                        onChange={handleChange}
                        placeholder="Enter your email"
                        className={`w-full rounded-md border ${emailError ? "border-red-500" : "border-gray-300"} pl-10 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                    </div>
                    {emailError && (
                      <div className="flex items-start mt-1">
                        <AlertCircle size={16} className="text-red-500 mr-1 mt-0.5" />
                        <p className="text-red-500 text-xs">{emailError}</p>
                      </div>
                    )}
                  </div>

                  <div>
                    <button
                      className="w-full px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center justify-center gap-2 transition duration-200"
                      onClick={handleSendOtp}
                    >
                      <Send size={18} />
                      <span>Send OTP</span>
                    </button>
                  </div>

                  <div className="text-center text-sm text-gray-500">
                    We'll send a verification code to this email to reset your password
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="text-center mb-4">
                    <div className="flex justify-center mb-4">
                      <div className="h-16 w-16 rounded-full bg-blue-50 flex items-center justify-center">
                        <KeyRound size={32} className="text-blue-500" />
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">
                      Enter the 6-digit code sent to
                    </p>
                    <p className="font-semibold text-gray-800">{email}</p>
                  </div>

                  <div>
                    <div
                      className="flex justify-between gap-2"
                      onPaste={pasteOTP}
                    >
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          type="text"
                          maxLength="1"
                          name={`otp${index}`}
                          value={digit}
                          onChange={handleChange}
                          onKeyDown={(e) => handleKeyDown(e, index)}
                          className={`h-12 w-12 md:h-14 md:w-14 text-center rounded-md border ${otpError ? "border-red-500" : "border-gray-300"
                            } text-xl focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          ref={(el) => (otpRefs.current[index] = el)}
                        />
                      ))}
                    </div>
                    {otpError && (
                      <div className="flex items-start justify-center mt-2">
                        <AlertCircle size={16} className="text-red-500 mr-1 mt-0.5" />
                        <p className="text-red-500 text-xs">{otpError}</p>
                      </div>
                    )}
                  </div>

                  <div>
                    <button
                      className="w-full px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center justify-center gap-2 transition duration-200"
                      onClick={handleEnterOTP}
                    >
                      <ShieldCheck size={18} />
                      <span>Verify OTP</span>
                    </button>
                  </div>

                  <div className="flex justify-between text-sm pt-2">
                    <button
                      className="text-blue-500 hover:text-blue-700"
                      onClick={() => setOtpSent(false)}
                    >
                      Use a different email
                    </button>
                    <button
                      className="text-blue-500 hover:text-blue-700"
                      onClick={resendOTP}
                    >
                      Resend OTP
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right side - Information Banner */}
          <div className="hidden md:block w-1/2 bg-blue-500">
            <div className="h-full flex items-center justify-center p-12">
              <div className="text-white max-w-md">
                <h2 className="text-3xl font-bold mb-6">Password Recovery</h2>
                <p className="text-lg mb-6">
                  Follow these simple steps to reset your password and regain access to your account.
                </p>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-white text-blue-500 flex items-center justify-center font-bold mt-0.5">1</div>
                    <div>
                      <p className="font-semibold">Enter your email</p>
                      <p className="text-sm opacity-90">Provide the email address associated with your account</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-white text-blue-500 flex items-center justify-center font-bold mt-0.5">2</div>
                    <div>
                      <p className="font-semibold">Verify with OTP</p>
                      <p className="text-sm opacity-90">Enter the 6-digit code sent to your email</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-white text-blue-500 flex items-center justify-center font-bold mt-0.5">3</div>
                    <div>
                      <p className="font-semibold">Create new password</p>
                      <p className="text-sm opacity-90">Set a secure password to protect your account</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;